/* Paywall — modal & inline PaywallScreen variants (dark Apple Fitness style).
   Used when a Free user opens a Pro feature.
*/
import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { Lock, Sparkles, Crown, Check, X } from "lucide-react";
import { useSubscription } from "@/hooks/useSubscription";

const DEFAULT_BENEFITS = [
  "ORIO Walk + Match + SOS Emergenza",
  "Cassetto illimitato + PDF veterinario",
  "Cani illimitati · Radar completo",
  "AI illimitata · Stats avanzate",
];

export default function Paywall({ open, onClose, feature = "questa funzione", benefits }) {
  const navigate = useNavigate();
  const { canStartTrial, startTrial } = useSubscription();
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  const rows = benefits && benefits.length ? benefits : DEFAULT_BENEFITS;

  const handleStartTrial = async () => {
    const r = await startTrial();
    if (r) onClose?.();
  };

  return createPortal(
    <div className="fixed inset-0 z-[1250] flex items-end sm:items-center justify-center p-3"
      data-testid="paywall-modal" onClick={onClose}>
      <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(14px)" }} />
      <div className="relative w-full max-w-md rounded-t-[28px] sm:rounded-[28px] overflow-hidden border text-white"
        style={{
          background:
            "radial-gradient(120% 60% at 0% 0%, rgba(255,122,26,0.28) 0%, rgba(255,122,26,0) 60%), " +
            "linear-gradient(180deg, #14100C 0%, #0B0806 55%, #050403 100%)",
          borderColor: "rgba(255,255,255,0.12)",
          boxShadow: "0 40px 80px -20px rgba(0,0,0,0.6), 0 24px 48px -12px rgba(255,122,26,0.35)",
        }}
        onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose}
          data-testid="paywall-close"
          className="absolute right-3 top-3 h-8 w-8 rounded-full flex items-center justify-center z-10 border"
          style={{ background: "rgba(255,255,255,0.06)", borderColor: "rgba(255,255,255,0.12)" }}>
          <X className="h-4 w-4 text-white/85" />
        </button>

        <div className="relative p-6 sm:p-7">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.20em] text-[#0F0F0F]"
            style={{ background: "linear-gradient(135deg, #FFB020 0%, #F4B83C 100%)" }}>
            <Crown className="h-3 w-3" /> ORIO Pro
          </div>
          <h2 className="font-display text-3xl tracking-tight mt-3 leading-[1.05]">
            Sblocca {feature}<br />
            <span className="orio-grad-text">con ORIO Pro.</span>
          </h2>
          <p className="text-[13px] text-white/65 mt-2 leading-relaxed">
            Con il piano gratuito esplori l&apos;app. Le funzioni operative si attivano con Pro.
          </p>

          <div className="mt-5 space-y-2">
            {rows.map((b) => (
              <div key={b} className="flex items-start gap-2.5 text-[13px]">
                <span className="h-5 w-5 rounded-full flex items-center justify-center shrink-0 mt-px"
                  style={{ background: "rgba(52,211,153,0.20)", border: "1px solid rgba(52,211,153,0.40)" }}>
                  <Check className="h-3 w-3 text-[#34D399]" />
                </span>
                <span className="text-white/85">{b}</span>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-2xl border p-4"
               style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.10)", backdropFilter: "blur(10px)" }}>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-3xl tabular-nums">4,99 €</span>
              <span className="text-white/55 text-sm">/ mese</span>
              <span className="ml-auto text-[11px] text-white/45">o €40/anno</span>
            </div>
            <div className="text-[11px] text-white/55 mt-0.5">
              {canStartTrial ? "30 giorni gratis · nessuna carta" : "Disdici in un tap"}
            </div>
          </div>

          {canStartTrial ? (
            <button data-testid="paywall-start-trial" onClick={handleStartTrial}
              className="w-full mt-5 py-3.5 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                       boxShadow: "0 22px 40px -14px rgba(233,69,69,0.75), 0 0 0 6px rgba(255,255,255,0.04)" }}>
              <Sparkles className="h-4 w-4" /> Inizia 30 giorni gratis
            </button>
          ) : (
            <button data-testid="paywall-subscribe"
              onClick={() => { onClose?.(); navigate("/billing"); }}
              className="w-full mt-5 py-3.5 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2"
              style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                       boxShadow: "0 22px 40px -14px rgba(233,69,69,0.75), 0 0 0 6px rgba(255,255,255,0.04)" }}>
              Vedi i piani
            </button>
          )}

          <button data-testid="paywall-later" onClick={onClose}
            className="w-full mt-2 py-2 text-[12px] text-white/55 hover:text-white/85">
            Continua a esplorare
          </button>
        </div>
      </div>
    </div>
  , document.body);
}

/* Inline full-page paywall variant — used by ProGate to guard entire routes. */
export function PaywallScreen({ feature, title, description }) {
  const navigate = useNavigate();
  const { canStartTrial, startTrial } = useSubscription();

  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-4 py-12 relative text-white"
      style={{
        background:
          "radial-gradient(140% 60% at 0% 0%, rgba(255,122,26,0.20) 0%, rgba(255,122,26,0) 55%), " +
          "linear-gradient(180deg, #14100C 0%, #0B0806 55%, #050403 100%)",
      }}>
      <div className="max-w-md text-center">
        <div className="mx-auto h-14 w-14 rounded-2xl flex items-center justify-center"
          style={{ background: "rgba(255,176,32,0.14)", color: "#F4B83C", border: "1px solid rgba(255,176,32,0.35)" }}>
          <Lock className="h-6 w-6" />
        </div>
        <div className="mt-4 text-[10px] uppercase tracking-[0.24em] text-white/55">Riservato a ORIO Pro</div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-1 leading-tight text-white">
          {title || feature}
        </h1>
        <p className="text-[13.5px] text-white/60 mt-2 leading-relaxed">
          {description || "Questa sezione è disponibile per gli abbonati ORIO Pro. Prova 30 giorni gratis, disdici in un tap."}
        </p>
        {canStartTrial ? (
          <button data-testid="paywall-screen-start-trial" onClick={startTrial}
            className="mt-6 px-6 py-3.5 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                     boxShadow: "0 22px 40px -14px rgba(233,69,69,0.75), 0 0 0 6px rgba(255,255,255,0.04)" }}>
            <Sparkles className="h-4 w-4" /> Inizia 30 giorni gratis
          </button>
        ) : (
          <button data-testid="paywall-screen-subscribe" onClick={() => navigate("/billing")}
            className="mt-6 px-6 py-3.5 rounded-full text-white font-semibold text-[14px] inline-flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg, #FF7A1A 0%, #E94545 100%)",
                     boxShadow: "0 22px 40px -14px rgba(233,69,69,0.75), 0 0 0 6px rgba(255,255,255,0.04)" }}>
            Vedi i piani
          </button>
        )}
        <div className="mt-4">
          <Link to="/app" className="text-[12px] text-white/45 hover:text-white/85">Torna alla home</Link>
        </div>
      </div>
    </div>
  );
}
